import { c as _c } from "react/compiler-runtime";
import { Button, IconButton, Tooltip, useTranslationContext } from 'platform/components';
import { HStack } from 'platform/foundation-tw';
import { match } from 'ts-pattern';
import { Nullish, suffixTestId, TestIdProps } from 'shared/web';
import { useEagGridConfig } from '../../context/useEagGridConfig';
import { useIsMobileView } from '../../hooks/useIsMobileView';
import { usePresetState } from '../../hooks/usePresetState';
import { useSmartSearchEnabled, usePresetsEnabled, useGlobalActionsEnabled, useFilterToolPanelVisible, useColumnToolPanelVisible, useSettingsToolPanelVisible } from '../../store/selectors';
import { FeGridSettings, GetDataQueryResponse, TransformedDefinitionApiResponse } from '../../types/Api';
import { EagColumnState } from '../../types/EagColumnState';
import { EagGridProps } from '../../types/EagGridProps';
import { ToolpanelType } from '../../types/ToolpanelType';
import { getPresetStates } from '../../utils/getPresetStates';
import { isPresetCustom } from '../../utils/isPresetCustom';
import { refreshDatagrid } from '../../utils/refreshDatagrid';
import { PresetsPanelRenderer } from '../PresetPanel/PresetPanel';
import { ExportButton } from './components/ExportButton/ExportButton';
import { LoadingBar } from './components/LoadingBar/LoadingBar';
import { MobilePresetsButton } from './components/MobilePresetsButton/MobilePresetsButton';
import { QuickFilterInput } from './components/QuickFilterInput/QuickFilterInput';
import { SharePresetButton } from './components/SharePresetButton/SharePresetButton';
export type GridHeaderProps = {
  dataGridSettings: FeGridSettings;
  dataGridProps: EagGridProps;
  definition: TransformedDefinitionApiResponse;
  dataQuery: GetDataQueryResponse | Nullish;
  refreshData: () => void;
  columnStates: EagColumnState[];
  activePanel: ToolpanelType | Nullish;
  onTogglePanel: (panel: ToolpanelType) => void;
  isFilterActive: boolean;
  onResetFilters: () => void;
} & TestIdProps;
const getPanelPriority = (activePanel: ToolpanelType | Nullish, panel: ToolpanelType) => match(activePanel === panel).with(true, () => 'secondary' as const).otherwise(() => undefined);
const getFilterPriority = (activePanel: ToolpanelType | Nullish, isFilterActive: boolean) => match({
  isOpen: activePanel === ToolpanelType.filter,
  isFilterActive
}).with({
  isFilterActive: true
}, () => 'primary' as const).with({
  isOpen: true
}, () => 'secondary' as const).otherwise(() => undefined);
export function GridHeader(props) {
  const $ = _c(76);
  const t = useTranslationContext();
  const {
    buildAbsoluteUrl
  } = useEagGridConfig();
  const isMobile = useIsMobileView();
  const smartSearchEnabled = useSmartSearchEnabled();
  const presetsEnabled = usePresetsEnabled();
  const globalActionsEnabled = useGlobalActionsEnabled();
  const filterToolPanelVisible = useFilterToolPanelVisible();
  const columnToolPanelVisible = useColumnToolPanelVisible();
  const settingsToolPanelVisible = useSettingsToolPanelVisible();
  const showHeaderPanel = smartSearchEnabled || presetsEnabled || globalActionsEnabled;
  const presetState = usePresetState();
  let t0;
  if ($[0] !== props.columnStates) {
    t0 = getPresetStates(props.columnStates);
    $[0] = props.columnStates;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const currentColumnSettings = t0;
  let t1;
  if ($[2] !== currentColumnSettings || $[3] !== presetState || $[4] !== props.dataGridSettings) {
    t1 = (name, desc) => presetState.createPreset(name, desc, currentColumnSettings, props.dataGridSettings);
    $[2] = currentColumnSettings;
    $[3] = presetState;
    $[4] = props.dataGridSettings;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const createPresetAdapter = t1;
  let t2;
  if ($[6] !== currentColumnSettings || $[7] !== presetState || $[8] !== props.dataGridSettings) {
    t2 = presetId => presetState.updatePreset(presetId, currentColumnSettings, props.dataGridSettings);
    $[6] = currentColumnSettings;
    $[7] = presetState;
    $[8] = props.dataGridSettings;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const updatePresetAdapter = t2;
  if (!showHeaderPanel) {
    return null;
  }
  let t3;
  if ($[10] !== props) {
    t3 = () => {
      props.refreshData();
      refreshDatagrid(props.dataGridProps.gridCode);
    };
    $[10] = props;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleRefreshData = t3;
  let t4;
  if ($[12] !== presetState.activePreset) {
    t4 = isPresetCustom(presetState.activePreset);
    $[12] = presetState.activePreset;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const isPresetActive = t4;
  if (isMobile) {
    let t5;
    if ($[14] !== props) {
      t5 = suffixTestId("headerWrapper", props);
      $[14] = props;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== props || $[17] !== smartSearchEnabled) {
      t6 = smartSearchEnabled && <div className="flex-1 min-w-0"><QuickFilterInput data-testid={props["data-testid"]} /></div>;
      $[16] = props;
      $[17] = smartSearchEnabled;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== isPresetActive || $[20] !== presetsEnabled || $[21] !== props) {
      t7 = presetsEnabled && <MobilePresetsButton isPresetActive={isPresetActive} activePanel={props.activePanel} onTogglePanel={props.onTogglePanel} data-testid={props["data-testid"]} />;
      $[19] = isPresetActive;
      $[20] = presetsEnabled;
      $[21] = props;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t8;
    if ($[23] !== columnToolPanelVisible || $[24] !== filterToolPanelVisible || $[25] !== globalActionsEnabled || $[26] !== handleRefreshData || $[27] !== props || $[28] !== settingsToolPanelVisible) {
      t8 = globalActionsEnabled && <HStack justify="flex-end" spacing={1}><IconButton icon="navigation/refresh" onClick={handleRefreshData} />{filterToolPanelVisible && <IconButton icon="content/filter_list" onClick={() => props.onTogglePanel(ToolpanelType.filter)} priority={getFilterPriority(props.activePanel, props.isFilterActive)} />}{columnToolPanelVisible && <IconButton icon="editor/table_chart" onClick={() => props.onTogglePanel(ToolpanelType.column)} priority={getPanelPriority(props.activePanel, ToolpanelType.column)} />}{settingsToolPanelVisible && <IconButton icon="action/settings" onClick={() => props.onTogglePanel(ToolpanelType.settings)} priority={getPanelPriority(props.activePanel, ToolpanelType.settings)} />}</HStack>;
      $[23] = columnToolPanelVisible;
      $[24] = filterToolPanelVisible;
      $[25] = globalActionsEnabled;
      $[26] = handleRefreshData;
      $[27] = props;
      $[28] = settingsToolPanelVisible;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    let t9;
    if ($[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
      t9 = <div className="flex items-center gap-2 px-2 py-2">{t6}{t7}{t8}</div>;
      $[30] = t6;
      $[31] = t7;
      $[32] = t8;
      $[33] = t9;
    } else {
      t9 = $[33];
    }
    let t10;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <LoadingBar />;
      $[34] = t10;
    } else {
      t10 = $[34];
    }
    let t11;
    if ($[35] !== t5 || $[36] !== t9) {
      t11 = <div className="relative" data-testid={t5}>{t9}{t10}</div>;
      $[35] = t5;
      $[36] = t9;
      $[37] = t11;
    } else {
      t11 = $[37];
    }
    return t11;
  }
  let t5;
  if ($[38] !== props) {
    t5 = suffixTestId("headerWrapper", props);
    $[38] = props;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  let t6;
  if ($[40] !== props || $[41] !== smartSearchEnabled) {
    t6 = smartSearchEnabled && <div className="pr-1.5"><QuickFilterInput className="w-[240px] flex-shrink-0" data-testid={props["data-testid"]} /></div>;
    $[40] = props;
    $[41] = smartSearchEnabled;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  let t7;
  if ($[43] !== createPresetAdapter || $[44] !== presetState.activatePreset || $[45] !== presetState.activePreset || $[46] !== presetState.deactivatePreset || $[47] !== presetState.deletePreset || $[48] !== presetState.duplicatePreset || $[49] !== presetState.isPresetChanged || $[50] !== presetState.presets || $[51] !== presetState.renamePreset || $[52] !== presetState.resetOverrides || $[53] !== presetState.rowCountGetter || $[54] !== presetState.updatePresetPosition || $[55] !== presetsEnabled || $[56] !== props || $[57] !== updatePresetAdapter) {
    t7 = presetsEnabled && <PresetsPanelRenderer activePreset={presetState.activePreset} isPresetChanged={presetState.isPresetChanged} activatePreset={presetState.activatePreset} deactivatePreset={presetState.deactivatePreset} deletePreset={presetState.deletePreset} duplicatePreset={presetState.duplicatePreset} resetOverrides={presetState.resetOverrides} updatePreset={updatePresetAdapter} createPreset={createPresetAdapter} renamePreset={presetState.renamePreset} updatePresetPosition={presetState.updatePresetPosition} rowCountGetter={presetState.rowCountGetter} presets={presetState.presets} data-testid={props["data-testid"]} />;
    $[43] = createPresetAdapter;
    $[44] = presetState.activatePreset;
    $[45] = presetState.activePreset;
    $[46] = presetState.deactivatePreset;
    $[47] = presetState.deletePreset;
    $[48] = presetState.duplicatePreset;
    $[49] = presetState.isPresetChanged;
    $[50] = presetState.presets;
    $[51] = presetState.renamePreset;
    $[52] = presetState.resetOverrides;
    $[53] = presetState.rowCountGetter;
    $[54] = presetState.updatePresetPosition;
    $[55] = presetsEnabled;
    $[56] = props;
    $[57] = updatePresetAdapter;
    $[58] = t7;
  } else {
    t7 = $[58];
  }
  let t8;
  if ($[59] !== buildAbsoluteUrl || $[60] !== columnToolPanelVisible || $[61] !== filterToolPanelVisible || $[62] !== globalActionsEnabled || $[63] !== handleRefreshData || $[64] !== props || $[65] !== settingsToolPanelVisible || $[66] !== t) {
    t8 = globalActionsEnabled && <HStack grow={1} justify="flex-end" spacing={1}>{props.isFilterActive && <Button variant="dangerGhost" leftIcon="content/clear" title={t("page.datagrid.button.resetFilters")} onClick={props.onResetFilters} data-testid={suffixTestId("resetFilters", props)} />}<Tooltip label={t("page.datagrid.button.refreshData")}><IconButton aria-label={t("page.datagrid.button.refreshData")} icon="navigation/refresh" onClick={handleRefreshData} data-testid={suffixTestId("refreshDataIconButton", props)} /></Tooltip>{buildAbsoluteUrl && <SharePresetButton data-testid={props["data-testid"]} />}<ExportButton data-testid={props["data-testid"]} />{filterToolPanelVisible && <Tooltip label={t("page.datagrid.labels.filters")}><IconButton icon="content/filter_list" onClick={() => props.onTogglePanel(ToolpanelType.filter)} priority={getFilterPriority(props.activePanel, props.isFilterActive)} data-testid={suffixTestId("toggleFilters", props)} /></Tooltip>}{columnToolPanelVisible && <Tooltip label={t("page.datagrid.labels.columnSetting")}><IconButton icon="editor/table_chart" onClick={() => props.onTogglePanel(ToolpanelType.column)} priority={getPanelPriority(props.activePanel, ToolpanelType.column)} data-testid={suffixTestId("toggleColumns", props)} /></Tooltip>}{settingsToolPanelVisible && <Tooltip label={t("page.datagrid.labels.settings")}><IconButton icon="action/settings" onClick={() => props.onTogglePanel(ToolpanelType.settings)} priority={getPanelPriority(props.activePanel, ToolpanelType.settings)} data-testid={suffixTestId("toggleSettings", props)} /></Tooltip>}</HStack>;
    $[59] = buildAbsoluteUrl;
    $[60] = columnToolPanelVisible;
    $[61] = filterToolPanelVisible;
    $[62] = globalActionsEnabled;
    $[63] = handleRefreshData;
    $[64] = props;
    $[65] = settingsToolPanelVisible;
    $[66] = t;
    $[67] = t8;
  } else {
    t8 = $[67];
  }
  let t9;
  if ($[68] !== t6 || $[69] !== t7 || $[70] !== t8) {
    t9 = <div className="flex items-center gap-1 px-2 py-2">{t6}{t7}{t8}</div>;
    $[68] = t6;
    $[69] = t7;
    $[70] = t8;
    $[71] = t9;
  } else {
    t9 = $[71];
  }
  let t10;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <LoadingBar />;
    $[72] = t10;
  } else {
    t10 = $[72];
  }
  let t11;
  if ($[73] !== t5 || $[74] !== t9) {
    t11 = <div className="relative" data-testid={t5}>{t9}{t10}</div>;
    $[73] = t5;
    $[74] = t9;
    $[75] = t11;
  } else {
    t11 = $[75];
  }
  return t11;
}